/**
 * smRuntime — per-SM mutable runtime state.
 * Written by StateMachineEditor (transition triggers) and updateSMTransitions(),
 * read every frame by GraphEvaluator.
 *
 * { [smNodeId]: { currentState, stateEnteredAt, transition, pickedClip } }
 *   pickedClip — the randomly-chosen clip name for the current state when
 *                state.data.clips[] is used (overrides state.data.clipName)
 */
import { devLog } from './devLog'
import { DEFAULT_TRANSITION_S } from './transitionDuration.js'

export const smRuntime = {}

/** Pick a random clip name from a state node's clips[] array (if any). */
export function pickClipForState(stateNode) {
  const clips = stateNode?.data?.clips
  if (Array.isArray(clips) && clips.length > 0) {
    return clips[Math.floor(Math.random() * clips.length)]
  }
  return stateNode?.data?.clipName ?? null
}

/**
 * Initialize a SM entry in smRuntime if not already present.
 * currentState starts as null — updateSMTransitions() will activate
 * the initial state on the first frame based on Entry transition rules.
 * @param {string} smNodeId
 */
export function initSM(smNodeId) {
  if (!smRuntime[smNodeId]) {
    smRuntime[smNodeId] = {
      currentState:   null,
      stateEnteredAt: null,
      transition:     null,
      pickedClip:     null,
    }
  }
}

/**
 * Trigger a crossfade transition to a new state.  Picks a fresh clip from
 * the target state's `clips[]` array if defined.
 * @param {string} smNodeId
 * @param {string} fromStateId
 * @param {string} toStateId
 * @param {number} duration  seconds
 * @param {object} [toStateNode]  inner node object for the target state (for pickClip)
 */
export function triggerTransition(smNodeId, fromStateId, toStateId, duration = DEFAULT_TRANSITION_S, toStateNode = null) {
  if (!smRuntime[smNodeId]) smRuntime[smNodeId] = {}
  // Preserve the FROM state's picked clip so evalState doesn't re-roll it every frame during the crossfade
  const fromPickedClip = smRuntime[smNodeId].pickedClip ?? null
  smRuntime[smNodeId].currentState   = toStateId
  smRuntime[smNodeId].stateEnteredAt = Date.now()
  const pickedClip = toStateNode ? pickClipForState(toStateNode) : null
  smRuntime[smNodeId].transition     = {
    from:          fromStateId,
    to:            toStateId,
    duration,
    startTime:     Date.now(),
    fromPickedClip,
  }
  if (toStateNode) {
    smRuntime[smNodeId].pickedClip = pickedClip
  }
  const clipInfo = pickedClip ? `clip: "${pickedClip}"` : '(subgraph / no clip)'
  devLog(`[SM] ${smNodeId} | "${fromStateId ?? 'none'}" → "${toStateId}" | ${clipInfo} | crossfade ${duration}s`)
}

/** Get the current active state ID for a SM, or null. */
export function getCurrentState(smNodeId) {
  return smRuntime[smNodeId]?.currentState ?? null
}
